// External Libraries (React, Context, Hooks, i18n)
import { useContext } from "react";
import { useTranslation } from "react-i18next";

// Third-Party Packages
import { Box, Card, Grid, Stack, Typography, useMediaQuery } from "@mui/material";

// Contexts
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Internal Constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Internal Services
import { httpPost } from "../../services/commonService";

// Internal Components
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";

function PersonallySponsored() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:576px)');
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);

  const columns = [
    {
      key: "AcctID",
      label: t("PersonallySponsored.Account ID"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "AcctName",
      label: t("PersonallySponsored.Account Name"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "ReplicationSiteName",
      label: t("PersonallySponsored.Site Name"),
      type: "text",
      isFilterVisible: false,
    },
    {
      key: "Email",
      label: t("PersonallySponsored.Email"),
      type: "text",
      isFilterVisible: true,
      isDropDown: false,
    },
    {
      key: "PackageType",
      label: t("PersonallySponsored.Qualification"),
      type: "text",
      isFilterVisible: false,
    },
    {
      key: "Phone",
      label: t("PersonallySponsored.Phone"),
      type: "text",
      isFilterVisible: false,
    },
    {
      key: "PlacementLeg",
      label: t("PersonallySponsored.Placement Leg"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "NumOfSales",
      label: t("PersonallySponsored.Num Of Sales"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "NumOfInstallations",
      label: t("PersonallySponsored.Num Of Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last30DayNumOfInstallations",
      label: t("PersonallySponsored.Last 30 Day Num Of Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last60DayNumOfInstallations",
      label: t("PersonallySponsored.Last 60 Day Num Of Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last90DayNumOfInstallations",
      label: t("PersonallySponsored.Last 90 Day Num Of Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "NumOfTeamSales",
      label: t("PersonallySponsored.Num Of Team Sales"),
      type: "number",
      isFilterVisible: false,
      isDropDown: false,
    },
    {
      key: "NumOfTeamInstallations",
      label: t("PersonallySponsored.Num Of Team Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last30DayNumOfTeamInstallations",
      label: t("PersonallySponsored.Last 30 Day Num Of Team Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last60DayNumOfTeamInstallations",
      label: t("PersonallySponsored.Last 60 Day Num Of Team Installations"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "Last90DayNumOfTeamInstallations",
      label: t("PersonallySponsored.Last 90 Day Num Of Team Installations"),
      type: "number",
      isFilterVisible: false,
    },
  ];

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const getPersonallySponserList =
      urlConstant.PersonallySponsored.PersonallySponsoredList;
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {
        AccountIdFilter: filters?.AcctID,
        AccountNameFilter: filters?.AcctName,
        AccountEmailFilter: filters?.Email,
        PlacementLegFilter: filters?.PlacementLeg,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        getPersonallySponserList,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        return {
          data: response.Value.Data.Data,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography
          color="secondary.light"
          display={{ xs: "flex", sm: "none" }}
          variant="h4"
          fontWeight={600}
          fontSize={"24px"}
        >
          {t("Reports")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Grid container spacing={2}>
                <Typography
                  color="secondary.light"
                  variant="h4"
                  fontWeight={600}
                >
                  {t("PersonallySponsored.Personally Sponsored")}
                </Typography>
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <DataCardGrid
                    onFetchData={fetchDataFunction}
                    columns={columns}
                    defaultItemsPerPage={4}
                    defaultColumnsPerGrid={2}
                    enableExport={true}
                    enablePagination={true}
                    uniqueId="commission-grid"
                    {...(isMobile ? { initialVisibleRows: 7 } : {})}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default PersonallySponsored;